/**
 * Generic Variant Selector Component
 *
 * A unified component for selecting variants that supports both:
 * - Single-select mode: Dropdown for choosing one variant (e.g., tea sets)
 * - Multi-select mode: Toggle button grid for choosing multiple (e.g., runes)
 *
 * Follows Inspector v2's DropdownOption pattern for type-safe option arrays.
 *
 * @example
 * ```tsx
 * // Single-select (tea sets)
 * <VariantSelector
 *   mode="single"
 *   options={TEA_SET_VARIANT_OPTIONS}
 *   value={currentVariant}
 *   onChange={setVariant}
 * />
 *
 * // Multi-select (runes)
 * <VariantSelector
 *   mode="multi"
 *   options={RUNE_VARIANT_OPTIONS}
 *   selected={selectedVariants}
 *   onToggle={handleToggle}
 * />
 * ```
 */

import {
  Dropdown,
  makeStyles,
  Option,
  ToggleButton,
  tokens,
} from "@fluentui/react-components";
import type { FC } from "react";

/**
 * A variant option with display label and value.
 * Following Inspector v2's DropdownOption pattern.
 */
export interface VariantOption<V extends string> {
  label: string;
  value: V;
}

/**
 * Props for single-select mode
 */
interface SingleSelectProps<V extends string> {
  mode: "single";
  value: V;
  onChange: (value: V) => void;
  selected?: never;
  onToggle?: never;
}

/**
 * Props for multi-select mode
 */
interface MultiSelectProps<V extends string> {
  mode: "multi";
  selected: V[];
  onToggle: (variant: V) => void;
  value?: never;
  onChange?: never;
}

/**
 * Common props for both modes
 */
interface CommonProps<V extends string> {
  /** Available variant options */
  options: readonly VariantOption<V>[];
  /** Optional label (defaults to "Variant" for single, "Variants" for multi) */
  label?: string;
}

export type VariantSelectorProps<V extends string> = CommonProps<V> &
  (SingleSelectProps<V> | MultiSelectProps<V>);

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  dropdown: {
    minWidth: "150px",
  },
  grid: {
    display: "grid",
    gap: tokens.spacingHorizontalXS,
    gridTemplateColumns: "repeat(4, 1fr)",
  },
  label: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
  toggleButton: {
    fontSize: tokens.fontSizeBase100,
    minWidth: "auto",
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`,
    textTransform: "capitalize",
  },
});

/**
 * Generic variant selector supporting single and multi-select modes.
 */
export function VariantSelector<V extends string>(
  props: VariantSelectorProps<V>,
): ReturnType<FC> {
  const styles = useStyles();
  const { options, label, mode } = props;

  if (mode === "single") {
    const { value, onChange } = props;
    const defaultLabel = label ?? "Variant";

    // Find the selected option for display
    const selectedOption = options.find((opt) => opt.value === value);

    return (
      <div className={styles.container}>
        <span className={styles.label}>{defaultLabel}</span>
        <Dropdown
          className={styles.dropdown}
          onOptionSelect={(_, data) => {
            if (data.optionValue) {
              onChange(data.optionValue as V);
            }
          }}
          selectedOptions={[value]}
          value={selectedOption?.label ?? value}
        >
          {options.map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Dropdown>
      </div>
    );
  }

  // Multi-select mode
  const { selected, onToggle } = props;
  const defaultLabel = label ?? "Variants";

  return (
    <div className={styles.container}>
      <span className={styles.label}>
        {defaultLabel} ({selected.length} selected)
      </span>
      <div className={styles.grid}>
        {options.map((option) => (
          <ToggleButton
            appearance="subtle"
            checked={selected.includes(option.value)}
            className={styles.toggleButton}
            key={option.value}
            onClick={() => onToggle(option.value)}
            size="small"
          >
            {option.label}
          </ToggleButton>
        ))}
      </div>
    </div>
  );
}
